import {useEffect, useMemo, useState} from 'react'
import {useMutation} from '@apollo/client'
import {Alert, Badge, Button, Form, Modal, Spinner, Table} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {MERGE_DUPLICATE_CONTACTS} from '../../../gql/mutations/contactsMutation'

export type MergeContactCandidate = {
  id: number
  fullName?: string | null
  email?: string | null
  phone?: string | null
  dateBirth?: string | null
  policyCount: number
  created?: string | null
  reasons?: string[]
}

type Props = {
  show: boolean
  groups: MergeContactCandidate[][]
  analyzedCount?: number
  truncated?: boolean
  onHide: () => void
  onMerged: () => void
}

export const ModalMergeContacts = ({
  show,
  groups,
  analyzedCount,
  truncated,
  onHide,
  onMerged,
}: Props) => {
  const [remainingGroups, setRemainingGroups] = useState(groups)
  const [selected, setSelected] = useState<Record<number, number[]>>({})
  const [primary, setPrimary] = useState<Record<number, number>>({})
  const [mergingGroup, setMergingGroup] = useState<number | null>(null)
  const [mergingAll, setMergingAll] = useState(false)
  const [mergeResults, setMergeResults] = useState<string[]>([])
  const [mergeErrors, setMergeErrors] = useState<string[]>([])
  const [mergeContacts] = useMutation(MERGE_DUPLICATE_CONTACTS)

  const resetGroupSelection = (nextGroups: MergeContactCandidate[][]) => {
    const nextSelected: Record<number, number[]> = {}
    const nextPrimary: Record<number, number> = {}
    nextGroups.forEach((group, index) => {
      nextSelected[index] = group.map((contact) => contact.id)
      if (group[0]) nextPrimary[index] = group[0].id
    })
    setSelected(nextSelected)
    setPrimary(nextPrimary)
  }

  useEffect(() => {
    setRemainingGroups(groups)
    resetGroupSelection(groups)
    setMergeResults([])
    setMergeErrors([])
  }, [groups, show])

  const selectedTotal = useMemo(
    () => Object.values(selected).reduce((total, ids) => total + ids.length, 0),
    [selected]
  )
  const mergeableGroupCount = useMemo(
    () =>
      remainingGroups.filter((_, index) => {
        const ids = selected[index] || []
        return ids.length >= 2 && ids.includes(primary[index])
      }).length,
    [primary, remainingGroups, selected]
  )

  const toggleContact = (groupIndex: number, contactId: number) => {
    setSelected((current) => {
      const ids = current[groupIndex] || []
      const nextIds = ids.includes(contactId)
        ? ids.filter((id) => id !== contactId)
        : [...ids, contactId]
      return {...current, [groupIndex]: nextIds}
    })
  }

  const mergeGroup = async (groupIndex: number) => {
    const ids = selected[groupIndex] || []
    const primaryId = primary[groupIndex]
    if (ids.length < 2 || !ids.includes(primaryId)) {
      toast.error('Select at least two contacts and choose one of them as the primary contact.')
      return
    }

    setMergingGroup(groupIndex)
    try {
      const result = await mergeContacts({
        variables: {primaryId, duplicateIds: ids.filter((id) => id !== primaryId)},
      })
      const payload = result.data?.mergeDuplicateContacts
      if (!payload?.success) throw new Error(payload?.errors?.[0]?.message || 'Unable to merge contacts.')
      toast.success(`${payload.mergedCount} duplicate contact(s) merged.`)
      const keptContact = remainingGroups[groupIndex]?.find((contact) => contact.id === primaryId)
      const nextGroups = remainingGroups.filter((_, index) => index !== groupIndex)
      setRemainingGroups(nextGroups)
      resetGroupSelection(nextGroups)
      setMergeResults((current) => [
        `${payload.mergedCount} duplicate contact(s) merged into ${
          keptContact?.fullName || `contact #${primaryId}`
        }.`,
        ...current,
      ])
      onMerged()
    } catch (error: any) {
      toast.error(error.message || 'Unable to merge contacts.')
    } finally {
      setMergingGroup(null)
    }
  }

  const mergeAllSelectedGroups = async () => {
    const operations = remainingGroups
      .map((group, groupIndex) => ({
        group,
        groupIndex,
        ids: selected[groupIndex] || [],
        primaryId: primary[groupIndex],
      }))
      .filter(({ids, primaryId}) => ids.length >= 2 && ids.includes(primaryId))

    if (!operations.length) {
      toast.error('Select at least two contacts in one or more groups.')
      return
    }

    setMergingAll(true)
    setMergeErrors([])
    const successfulIndexes = new Set<number>()
    const successfulResults: string[] = []
    const failedResults: string[] = []

    for (const operation of operations) {
      const keptContact = operation.group.find((contact) => contact.id === operation.primaryId)
      try {
        const result = await mergeContacts({
          variables: {
            primaryId: operation.primaryId,
            duplicateIds: operation.ids.filter((id) => id !== operation.primaryId),
          },
        })
        const payload = result.data?.mergeDuplicateContacts
        if (!payload?.success)
          throw new Error(payload?.errors?.[0]?.message || 'Unable to merge contacts.')
        successfulIndexes.add(operation.groupIndex)
        successfulResults.push(
          `${payload.mergedCount} duplicate contact(s) merged into ${
            keptContact?.fullName || `contact #${operation.primaryId}`
          }.`
        )
      } catch (error: any) {
        failedResults.push(
          `${keptContact?.fullName || `Group ${operation.groupIndex + 1}`}: ${
            error.message || 'Unable to merge contacts.'
          }`
        )
      }
    }

    const nextGroups = remainingGroups.filter((_, index) => !successfulIndexes.has(index))
    setRemainingGroups(nextGroups)
    resetGroupSelection(nextGroups)
    setMergeResults((current) => [...successfulResults, ...current])
    setMergeErrors(failedResults)
    setMergingAll(false)

    if (successfulIndexes.size) {
      toast.success(`${successfulIndexes.size} candidate group(s) merged.`)
      onMerged()
    }
    if (failedResults.length) toast.error(`${failedResults.length} group(s) could not be merged.`)
  }

  return (
    <Modal show={show} onHide={onHide} size='xl' backdrop='static' centered>
      <Modal.Header closeButton>
        <Modal.Title>Review duplicate contacts</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{maxHeight: '70vh', overflowY: 'auto'}}>
        {typeof analyzedCount === 'number' && (
          <Alert variant='info'>
            Analyzed {analyzedCount} filtered contacts and found {remainingGroups.length} candidate group(s).
            {truncated && ' Only the first 5,000 contacts were analyzed.'}
          </Alert>
        )}
        {!!remainingGroups.length && (
          <div className='d-flex justify-content-end mb-3'>
            <Button
              onClick={() => void mergeAllSelectedGroups()}
              disabled={mergingAll || mergingGroup !== null || mergeableGroupCount === 0}
            >
              {mergingAll && <Spinner animation='border' size='sm' className='me-2' />}
              Merge all selected groups ({mergeableGroupCount})
            </Button>
          </div>
        )}
        {!!mergeResults.length && (
          <Alert variant='success'>
            {mergeResults.map((result, index) => (
              <div key={`${result}-${index}`}>{result}</div>
            ))}
            <strong>{remainingGroups.length} candidate group(s) remaining.</strong>
          </Alert>
        )}
        {!!mergeErrors.length && (
          <Alert variant='danger'>
            <strong>Some groups could not be merged:</strong>
            {mergeErrors.map((result, index) => (
              <div key={`${result}-${index}`}>{result}</div>
            ))}
          </Alert>
        )}
        {!remainingGroups.length && (
          <Alert variant='success'>No pending duplicate groups remain.</Alert>
        )}
        {remainingGroups.map((group, groupIndex) => (
          <div className='border rounded mb-4' key={group.map((contact) => contact.id).join('-')}>
            <div className='d-flex justify-content-between align-items-center bg-light px-4 py-3'>
              <div>
                <strong>Candidate group {groupIndex + 1}</strong>
                <span className='text-muted ms-2'>Select records to merge and which one to keep.</span>
              </div>
              <Button
                size='sm'
                disabled={mergingAll || mergingGroup !== null || (selected[groupIndex]?.length || 0) < 2}
                onClick={() => void mergeGroup(groupIndex)}
              >
                {mergingGroup === groupIndex && <Spinner animation='border' size='sm' className='me-2' />}
                Merge selected
              </Button>
            </div>
            <Table responsive hover className='mb-0 align-middle'>
              <thead>
                <tr>
                  <th>Merge</th>
                  <th>Keep</th>
                  <th>Contact</th>
                  <th>Phone / Email</th>
                  <th>Date of birth</th>
                  <th>Policies</th>
                  <th>Match reason</th>
                </tr>
              </thead>
              <tbody>
                {group.map((contact) => {
                  const isSelected = (selected[groupIndex] || []).includes(contact.id)
                  return (
                    <tr key={contact.id}>
                      <td>
                        <Form.Check
                          checked={isSelected}
                          onChange={() => toggleContact(groupIndex, contact.id)}
                        />
                      </td>
                      <td>
                        <Form.Check
                          type='radio'
                          name={`primary-contact-${groupIndex}`}
                          checked={primary[groupIndex] === contact.id}
                          disabled={!isSelected}
                          onChange={() => setPrimary((current) => ({...current, [groupIndex]: contact.id}))}
                        />
                      </td>
                      <td>
                        <strong>{contact.fullName || `Contact #${contact.id}`}</strong>
                        <div className='text-muted'>ID {contact.id}</div>
                      </td>
                      <td>
                        <div>{contact.phone || '—'}</div>
                        <div className='text-muted'>{contact.email || '—'}</div>
                      </td>
                      <td>{contact.dateBirth || '—'}</td>
                      <td>
                        <Badge
                          pill
                          style={{
                            backgroundColor: '#3e97ff',
                            color: '#ffffff',
                            minWidth: 28,
                            fontWeight: 700,
                            fontSize: '0.8rem',
                          }}
                        >
                          {contact.policyCount}
                        </Badge>
                      </td>
                      <td>{contact.reasons?.join(', ') || 'Manually selected'}</td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </div>
        ))}
        {!!selectedTotal && (
          <small className='text-muted'>{selectedTotal} contact records currently selected.</small>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant='secondary'
          onClick={onHide}
          disabled={mergingAll || mergingGroup !== null}
        >
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
